/**
 * StageContext - React context for hot-swapping Babylon stages
 *
 * Provides:
 * - Stage state (current stage ID, loading state)
 * - loadStage() function to switch stages
 * - Access to StageManager for advanced usage
 */

import type { Scene } from "@babylonjs/core";
import {
  EventBus,
  InteractionManager,
  PhysicsConfigManager,
  PhysicsManager,
  type RuneVariant,
  type StageConfig,
  StageConfigManager,
  StageManager,
  type StagePack,
  type VisualEffectConfig,
  VisualEffectsConfigManager,
} from "@tony/nl-babylon";
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useTheme } from "@/context/ThemeContext";

/**
 * Registry of available stage packs
 */
const STAGE_PACKS: Record<string, StagePack> = {};

/**
 * Register a stage pack
 */
export function registerStagePack(pack: StagePack): void {
  STAGE_PACKS[pack.id] = pack;
}

/**
 * Get a registered stage pack
 */
export function getStagePack(id: string): StagePack | undefined {
  return STAGE_PACKS[id];
}

/**
 * Get all registered stage pack IDs
 */
export function getStagePackIds(): string[] {
  return Object.keys(STAGE_PACKS);
}

/**
 * Load mode for stage switching
 * - "open": Clear existing stage before loading (default)
 * - "insert": Append to existing stage meshes
 */
export type StageLoadMode = "open" | "insert";

/**
 * Stage context value
 */
export interface StageContextValue {
  /** Current stage ID or null if no stage loaded */
  currentStage: string | null;
  /** Whether a stage is currently loading */
  isLoading: boolean;
  /** Whether the stage system is initialized */
  isInitialized: boolean;
  /** Whether a stage transition is pending (React 19 useTransition) */
  isStageTransitioning: boolean;
  /** Load a stage by ID, optionally in insert mode, with optional config overrides */
  loadStage: (
    stageId: string,
    mode?: StageLoadMode,
    configOverrides?: Partial<StageConfig>,
  ) => Promise<void>;
  /** Reset a stage (force reload to default state, even if already loaded) */
  resetStage: (stageId: string) => Promise<void>;
  /** Clear the current stage */
  clearStage: () => Promise<void>;
  /** Get the stage manager (for advanced usage) */
  getStageManager: () => StageManager | null;
  /** Get the physics manager (for advanced usage) */
  getPhysicsManager: () => PhysicsManager | null;
  /** Get the interaction manager (for advanced usage) */
  getInteractionManager: () => InteractionManager | null;
  /** Get the stage config manager (for advanced usage) */
  getStageConfigManager: () => StageConfigManager | null;
  /** Get the physics config manager (for advanced usage) */
  getPhysicsConfigManager: () => PhysicsConfigManager | null;
  /** Get the visual effects config manager (for advanced usage) */
  getVisualEffectsConfigManager: () => VisualEffectsConfigManager | null;
}

const StageContext = createContext<StageContextValue | null>(null);

/**
 * Props for StageProvider
 */
export interface StageProviderProps {
  children: ReactNode;
}

/**
 * StageProvider - Provides stage context to child components
 *
 * Must be used within a ThemeProvider for theme integration.
 */
export const StageProvider: FC<StageProviderProps> = ({ children }) => {
  "use no memo";

  const { theme, effectiveColorScheme } = useTheme();

  const [currentStage, setCurrentStage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // React 19: useTransition for non-blocking stage state updates
  const [isStageTransitioning, startTransition] = useTransition();

  // Manager refs
  const eventBusRef = useRef<EventBus | null>(null);
  const stageManagerRef = useRef<StageManager | null>(null);
  const physicsManagerRef = useRef<PhysicsManager | null>(null);
  const interactionManagerRef = useRef<InteractionManager | null>(null);
  const stageConfigManagerRef = useRef<StageConfigManager | null>(null);
  const physicsConfigManagerRef = useRef<PhysicsConfigManager | null>(null);
  const visualEffectsConfigManagerRef =
    useRef<VisualEffectsConfigManager | null>(null);
  const sceneRef = useRef<Scene | null>(null);

  // Ref to track currentStage for comparison in callbacks without causing re-renders
  // This breaks the dependency cycle: loadStage -> setCurrentStage -> new loadStage -> useEffect
  const currentStageRef = useRef<string | null>(null);
  currentStageRef.current = currentStage;

  /**
   * Initialize all managers (called when scene is ready)
   */
  const initializeManagers = useCallback(async (scene: Scene) => {
    // Guard: If already initialized with this scene, skip
    if (sceneRef.current === scene && stageManagerRef.current) {
      console.log("[StageContext] Already initialized for this scene");
      return;
    }

    setIsInitialized(false);
    currentStageRef.current = null;
    setCurrentStage(null);

    // If scene changed or managers exist, dispose old managers first
    if (stageManagerRef.current) {
      console.log("[StageContext] Disposing old managers before reinit");
      stageManagerRef.current.dispose();
      interactionManagerRef.current?.dispose();
      physicsManagerRef.current?.dispose();
      stageConfigManagerRef.current?.dispose();
      physicsConfigManagerRef.current?.dispose();
      visualEffectsConfigManagerRef.current?.dispose();
      eventBusRef.current?.dispose();
    }

    console.log("[StageContext] INITIALIZING_MANAGERS");

    // Create event bus
    const eventBus = new EventBus();
    eventBusRef.current = eventBus;

    // Initialize StageConfigManager (manages shared stage config state)
    const stageConfigManager = new StageConfigManager(eventBus);
    await stageConfigManager.initialize();
    stageConfigManagerRef.current = stageConfigManager;

    // Initialize PhysicsConfigManager (manages physics config state)
    const physicsConfigManager = new PhysicsConfigManager(eventBus);
    await physicsConfigManager.initialize();
    physicsConfigManagerRef.current = physicsConfigManager;

    // Initialize VisualEffectsConfigManager (manages visual effects config state)
    const visualEffectsConfigManager = new VisualEffectsConfigManager(eventBus);
    await visualEffectsConfigManager.initialize();
    visualEffectsConfigManagerRef.current = visualEffectsConfigManager;

    // Store EventBus and config managers in scene.metadata for Inspector v2
    scene.metadata = {
      ...scene.metadata,
      eventBus,
      physicsConfigManager,
      stageConfigManager,
      visualEffectsConfigManager,
    };
    sceneRef.current = scene;

    // Initialize PhysicsManager
    const physicsManager = new PhysicsManager(eventBus);
    await physicsManager.initialize();
    physicsManager.setScene(scene);
    await physicsManager.enablePhysics({ gravity: { x: 0, y: -9.81, z: 0 } });
    physicsManagerRef.current = physicsManager;

    // Initialize InteractionManager
    const interactionManager = new InteractionManager(eventBus);
    await interactionManager.initialize();
    interactionManager.setScene(scene);
    interactionManager.enableInteractions(physicsManager);
    interactionManagerRef.current = interactionManager;

    // Initialize StageManager
    const stageManager = new StageManager(eventBus);
    await stageManager.initialize();
    stageManager.setScene(scene);
    stageManager.setPhysicsManager(physicsManager);
    stageManager.setInteractionManager(interactionManager);
    stageManagerRef.current = stageManager;

    // Listen for stage config changes from Inspector v2 StageControls panel
    // StageConfigManager already updated itself before emitting this event
    // We just need to reload the stage to apply the new config
    eventBus.on("stageConfigChanged", (config: unknown) => {
      const typedConfig = config as {
        stageId: string;
        count?: number;
        scale?: number;
        variants?: RuneVariant[];
      };
      console.log("[StageContext] Received stageConfigChanged:", typedConfig);

      // Reload the stage to apply the new config
      const currentPackId = stageManagerRef.current?.getCurrentPackId();
      if (currentPackId === typedConfig.stageId) {
        stageManagerRef.current?.clearStage().then(() => {
          const pack = STAGE_PACKS[typedConfig.stageId];
          if (pack) {
            stageManagerRef.current?.loadPack(pack);
          }
        });
      }
    });

    // Listen for visual effects config changes from Inspector v2 StageControls panel
    eventBus.on("visualEffectsConfigChanged", (config: unknown) => {
      const typedConfig = config as {
        hover: VisualEffectConfig;
        selection: VisualEffectConfig;
      };
      console.log(
        "[StageContext] Received visualEffectsConfigChanged:",
        typedConfig,
      );

      // Apply effects to InteractionManager
      const interactionManager = interactionManagerRef.current;
      if (interactionManager) {
        interactionManager.setHoverEffects(typedConfig.hover);
        interactionManager.setSelectionEffects(typedConfig.selection);
      }
    });

    setIsInitialized(true);
    console.log("[StageContext] MANAGERS_INITIALIZED");
  }, []);

  /**
   * Load a stage by ID
   * @param stageId - The stage pack ID to load
   * @param mode - "open" (clear then load, default) or "insert" (append)
   * @param configOverrides - Optional partial config to merge (e.g., glbKey from route)
   */
  const loadStage = useCallback(
    async (
      stageId: string,
      mode: StageLoadMode = "open",
      configOverrides?: Partial<StageConfig>,
    ) => {
      const stageManager = stageManagerRef.current;
      if (!stageManager) {
        console.error("[StageContext] Cannot load stage - not initialized");
        return;
      }

      // For "open" mode, skip if same stage with same config already loaded
      // For "insert" mode, always load (creates more meshes)
      // Use ref to avoid dependency cycle (loadStage -> setCurrentStage -> new loadStage)
      let needsClearForGlbChange = false;
      if (mode === "open" && currentStageRef.current === stageId) {
        // Check if glbKey changed - if so, we need to reload with new GLB variant
        const currentGlbKey = stageConfigManagerRef.current?.config?.glbKey;
        const newGlbKey = configOverrides?.glbKey;
        if (currentGlbKey === newGlbKey) {
          console.log(
            `[StageContext] Stage ${stageId} already loaded with same config`,
          );
          return;
        }
        console.log(
          `[StageContext] Stage ${stageId} glbKey changed: ${currentGlbKey} -> ${newGlbKey}, reloading`,
        );
        // Mark that we need to clear first so StageManager.loadPack won't skip
        // (it checks currentPack.id which would match since pack ID is same)
        needsClearForGlbChange = true;
      }

      const pack = STAGE_PACKS[stageId];
      if (!pack) {
        console.error(`[StageContext] Stage pack not found: ${stageId}`);
        return;
      }

      setIsLoading(true);

      try {
        // Update theme before loading
        stageManager.setTheme({
          isDarkMode: effectiveColorScheme === "dark",
          themeId: theme,
        });

        // Initialize config managers (loads persisted config or defaults)
        // Pass overrides (e.g., glbKey from route query params)
        stageConfigManagerRef.current?.onStageLoaded(stageId, configOverrides);
        physicsConfigManagerRef.current?.onStageLoaded(stageId);
        visualEffectsConfigManagerRef.current?.onStageLoaded(stageId);

        if (mode === "insert") {
          // Insert mode: append without clearing
          await stageManager.insertPack(pack);
        } else {
          // Open mode: clear and load (default)
          // If glbKey changed, we must clear first so StageManager.loadPack
          // doesn't skip (it compares pack.id which is same for all drill variants)
          if (needsClearForGlbChange) {
            await stageManager.clearStage();
          }
          await stageManager.loadPack(pack);
        }

        // Store currentStageId in scene.metadata for Inspector v2 StageControls service
        const scene = sceneRef.current;
        if (scene) {
          scene.metadata = { ...scene.metadata, currentStageId: stageId };
        }

        // Emit stageLoaded event via EventBus so Inspector's StageControlsService updates
        eventBusRef.current?.emit("stageLoaded", { packId: stageId });

        startTransition(() => {
          setCurrentStage(stageId);
        });
      } catch (error) {
        console.error(`[StageContext] Failed to load stage: ${stageId}`, error);
      } finally {
        setIsLoading(false);
      }
    },
    [effectiveColorScheme, theme],
  );

  /**
   * Reset a stage (force reload even if same stage already loaded)
   * Clears and reloads the stage to its default state.
   */
  const resetStage = useCallback(
    async (stageId: string) => {
      const stageManager = stageManagerRef.current;
      if (!stageManager) {
        console.error("[StageContext] Cannot reset stage - not initialized");
        return;
      }

      const pack = STAGE_PACKS[stageId];
      if (!pack) {
        console.error(`[StageContext] Stage pack not found: ${stageId}`);
        return;
      }

      setIsLoading(true);

      try {
        // Clear the current stage
        await stageManager.clearStage();

        // Reset all config managers to defaults (clears localStorage)
        stageConfigManagerRef.current?.resetToDefaults();
        physicsConfigManagerRef.current?.resetToDefaults();
        visualEffectsConfigManagerRef.current?.resetToDefaults();

        // Reset current stage ref so loadStage doesn't skip
        currentStageRef.current = null;
        startTransition(() => {
          setCurrentStage(null);
        });

        // Update theme before loading
        stageManager.setTheme({
          isDarkMode: effectiveColorScheme === "dark",
          themeId: theme,
        });

        // Initialize fresh config managers (loads defaults for this stage)
        stageConfigManagerRef.current?.onStageLoaded(stageId);
        physicsConfigManagerRef.current?.onStageLoaded(stageId);
        visualEffectsConfigManagerRef.current?.onStageLoaded(stageId);

        // Load the stage fresh
        await stageManager.loadPack(pack);

        // Store currentStageId in scene.metadata for Inspector v2
        const scene = sceneRef.current;
        if (scene) {
          scene.metadata = { ...scene.metadata, currentStageId: stageId };
        }

        // Emit stageLoaded event
        eventBusRef.current?.emit("stageLoaded", { packId: stageId });

        startTransition(() => {
          setCurrentStage(stageId);
        });
      } catch (error) {
        console.error(
          `[StageContext] Failed to reset stage: ${stageId}`,
          error,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [effectiveColorScheme, theme],
  );

  /**
   * Clear the current stage
   */
  const clearStage = useCallback(async () => {
    const stageManager = stageManagerRef.current;
    if (!stageManager) {
      return;
    }

    await stageManager.clearStage();

    // Reset all config managers (keeps localStorage for when stage reloads)
    stageConfigManagerRef.current?.resetToDefaults();
    physicsConfigManagerRef.current?.resetToDefaults();
    visualEffectsConfigManagerRef.current?.resetToDefaults();

    // Clear currentStageId from scene.metadata
    const scene = sceneRef.current;
    if (scene) {
      scene.metadata = { ...scene.metadata, currentStageId: null };
    }

    startTransition(() => {
      setCurrentStage(null);
    });
  }, []);

  /**
   * Get managers (for advanced usage)
   */
  const getStageManager = useCallback(() => stageManagerRef.current, []);
  const getPhysicsManager = useCallback(() => physicsManagerRef.current, []);
  const getInteractionManager = useCallback(
    () => interactionManagerRef.current,
    [],
  );
  const getStageConfigManager = useCallback(
    () => stageConfigManagerRef.current,
    [],
  );
  const getPhysicsConfigManager = useCallback(
    () => physicsConfigManagerRef.current,
    [],
  );
  const getVisualEffectsConfigManager = useCallback(
    () => visualEffectsConfigManagerRef.current,
    [],
  );

  /**
   * Update theme when it changes
   * Uses useEffect instead of useMemo to avoid side effects during render
   */
  useEffect(() => {
    const stageManager = stageManagerRef.current;
    if (stageManager && isInitialized) {
      stageManager.setTheme({
        isDarkMode: effectiveColorScheme === "dark",
        themeId: theme,
      });
    }
  }, [effectiveColorScheme, isInitialized, theme]);

  const contextValue = useMemo<StageContextValue>(
    () => ({
      clearStage,
      currentStage,
      getInteractionManager,
      getPhysicsConfigManager,
      getPhysicsManager,
      getStageConfigManager,
      getStageManager,
      getVisualEffectsConfigManager,
      isInitialized,
      isLoading,
      isStageTransitioning,
      loadStage,
      resetStage,
    }),
    [
      clearStage,
      currentStage,
      getInteractionManager,
      getPhysicsConfigManager,
      getPhysicsManager,
      getStageConfigManager,
      getStageManager,
      getVisualEffectsConfigManager,
      isInitialized,
      isLoading,
      isStageTransitioning,
      loadStage,
      resetStage,
    ],
  );

  return (
    <StageContext value={contextValue}>
      <StageContextInternal initializeManagers={initializeManagers}>
        {children}
      </StageContextInternal>
    </StageContext>
  );
};

/**
 * Internal component to expose initializeManagers to children
 */
interface StageContextInternalProps {
  children: ReactNode;
  initializeManagers: (scene: Scene) => Promise<void>;
}

const StageContextInternalContext = createContext<{
  initializeManagers: (scene: Scene) => Promise<void>;
} | null>(null);

const StageContextInternal: FC<StageContextInternalProps> = ({
  children,
  initializeManagers,
}) => {
  const value = useMemo(() => ({ initializeManagers }), [initializeManagers]);
  return (
    <StageContextInternalContext value={value}>
      {children}
    </StageContextInternalContext>
  );
};

/**
 * Hook to access stage context
 */
export function useStage(): StageContextValue {
  const context = useContext(StageContext);
  if (!context) {
    throw new Error("useStage must be used within a StageProvider");
  }
  return context;
}

/**
 * Hook to access stage initialization function
 * Used by the canvas component to initialize managers when scene is ready
 */
export function useStageInitializer(): {
  initializeManagers: (scene: Scene) => Promise<void>;
} {
  const context = useContext(StageContextInternalContext);
  if (!context) {
    throw new Error("useStageInitializer must be used within a StageProvider");
  }
  return context;
}
